import { Inject, Pipe, PipeTransform } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { ChcpLaunchDarklyFlagKey } from '@cigna/chcp/shared/launch-darkly-util';
// TODO: resolve CHCP disables by bot
// eslint-disable-next-line @nx/enforce-module-boundaries
import { AuthFacade } from '@cigna/chcp/auth/data-access';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { FeatureTogglesFacade } from '@cigna/shared/angular/features-feature';

import { APP_CONTEXT, ENVIRONMENT_CONFIG, EnvironmentConfig } from './config';

interface ResourceLinkOptions {
  secure?: boolean;
  type?: 'page' | 'file';
  targetApp?: 'chcp' | 'evernorth' | 'both'; // 'both' will use appContext to send to appropriate resource library
}

const DRUPAL_RESOURCES_FLAG: ChcpLaunchDarklyFlagKey =
  'ftr-chcp-cloud9-team-web-resources-drupal-version';

@Pipe({
  name: 'resourceLink',
})
export class ResourceLinkPipe implements PipeTransform {
  constructor(
    @Inject(ENVIRONMENT_CONFIG) private _config: EnvironmentConfig,
    private authFacade: AuthFacade,
    @Inject(APP_CONTEXT) private appContext: string,
    private featureTogglesFacade: FeatureTogglesFacade,
  ) {}

  transform(
    path: string,
    {
      secure = true,
      type = 'page',
      targetApp = 'chcp',
    }: ResourceLinkOptions = {},
  ): Observable<string> {
    return this.featureTogglesFacade
      .featuresEnabled<ChcpLaunchDarklyFlagKey>([DRUPAL_RESOURCES_FLAG])
      .pipe(
        switchMap((useDrupal) =>
          this.buildLink(path, { secure, type, targetApp }, useDrupal),
        ),
      );
  }

  private buildLink(
    path: string,
    options: ResourceLinkOptions,
    useDrupal: boolean,
  ): Observable<string> {
    const {
      secure: isSecure = true,
      type = 'page',
      targetApp = 'chcp',
    } = options;
    const isPage = type === 'page' && !path.endsWith('.pdf');
    const appPath = this.resolveAppPath(targetApp);
    const isChcpTarget = this.isChcpResourceTarget(targetApp);

    if (isChcpTarget && isSecure && this.appContext === 'chcp') {
      return this.authFacade.isDentalUser$.pipe(
        map((isDental) =>
          this.formatLink({
            useDrupal,
            isChcpTarget,
            appPath,
            prefix: isPage ? (isDental ? '/dental' : '/medical') : '/secure',
            path,
            isPage,
          }),
        ),
      );
    }

    return of(
      this.formatLink({
        useDrupal: isChcpTarget && useDrupal,
        isChcpTarget,
        appPath,
        prefix: !isChcpTarget && isSecure && isPage ? '/behavioral' : '',
        path,
        isPage,
      }),
    );
  }

  private isChcpResourceTarget(targetApp: ResourceLinkOptions['targetApp']) {
    return (
      targetApp === 'chcp' ||
      (this.appContext === 'chcp' && targetApp === 'both')
    );
  }

  private resolveAppPath(targetApp: ResourceLinkOptions['targetApp']) {
    const isEvernorthPath =
      (this.appContext === 'evernorth' && targetApp !== 'chcp') ||
      (this.appContext === 'chcp' && targetApp === 'evernorth');

    return isEvernorthPath ? 'evernorth/provider' : 'chcp';
  }

  private formatLink({
    useDrupal,
    isChcpTarget,
    appPath,
    prefix,
    path,
    isPage,
  }: {
    useDrupal: boolean;
    isChcpTarget: boolean;
    appPath: string;
    prefix: string;
    path: string;
    isPage: boolean;
  }) {
    const cdnBase = isChcpTarget
      ? this._config.redirect.resourceLibrary.replace(
          'static.evernorth',
          'static.cigna',
        )
      : this._config.redirect.resourceLibrary.replace(
          'static.cigna',
          'static.evernorth',
        );

    return this.formatResourceLibraryUrl({
      useDrupal,
      cdnBase,
      drupalBase: this._config.redirect.resourceLibraryDrupal,
      appPath,
      prefix,
      path,
      isPage,
    });
  }

  private formatResourceLibraryUrl({
    useDrupal,
    cdnBase,
    drupalBase,
    appPath,
    prefix,
    path,
    isPage,
  }: {
    useDrupal: boolean;
    cdnBase: string;
    drupalBase?: string;
    appPath: string;
    prefix: string;
    path: string;
    isPage: boolean;
  }): string {
    const canUseDrupal = useDrupal && !!drupalBase;
    const base = canUseDrupal
      ? this.ensureTrailingSlash(drupalBase)
      : this.ensureTrailingSlash(cdnBase);
    const assetsPrefix = canUseDrupal ? '' : 'assets/';
    const htmlSuffix = isPage && !canUseDrupal ? '.html' : '';

    return `${base}${assetsPrefix}${appPath}${prefix}${path}${htmlSuffix}`;
  }

  private ensureTrailingSlash(url: string): string {
    if (!url) {
      return '';
    }

    return url.endsWith('/') ? url : `${url}/`;
  }
}
